/**
 * Lab insights on the lab page: analyse how the lab's students worked (each
 * student, and the class as a whole), follow the analysis, open the report.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Sparkles, Square } from 'lucide-react';
import { labInsights } from '../services/api';
import { Dialog, DialogHeader, DialogBody, DialogFooter } from './Dialog';
import { Chip, VERDICTS, errorMessage, whenOf } from './LabInsightsParts';

export const ACTIVE = ['preparing', 'analyzing', 'summarizing'];

const PROVIDERS = { 'azure-openai': 'Azure OpenAI', openai: 'OpenAI' };
const count = (n) => Number(n || 0).toLocaleString();

/** Where a run's requests went, and how many tokens they used (staff see tokens, never costs). */
function runUse(run) {
  const where = [PROVIDERS[run.provider], run.host].filter(Boolean).join(', ');
  const usage = run.usage || {};
  const tokens = usage.calls ? ` ${count(usage.calls)} model request${usage.calls === 1 ? '' : 's'}, ${count(usage.inputTokens)} tokens read and ${count(usage.outputTokens)} written.` : '';
  return { where: where ? ` (${where})` : '', tokens };
}

export function InsightProgress({ run }) {
  if (!run || !ACTIVE.includes(run.status)) return null;
  const { total = 0, done = 0, failed = 0 } = run.progress || {};
  const share = total ? Math.round(((done + failed) / total) * 100) : 0;
  const text = run.status === 'preparing'
    ? 'Finding each student\'s recording (starting the lab\'s snapshot storage if it was stopped)…'
    : run.status === 'summarizing' ? 'Writing the class summary…'
      : `Analysing students: ${done + failed} of ${total} done${failed ? ` (${failed} could not be analysed)` : ''}.`;
  return (
    <div role="status" aria-live="polite" style={{ display: 'grid', gap: 6 }}>
      <span style={{ fontSize: 14 }}>{text}</span>
      {run.status !== 'preparing' && (
        <div style={{ height: 6, borderRadius: 3, background: 'var(--bg-tertiary)', overflow: 'hidden' }}
          role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={share} aria-label="Analysis progress">
          <div style={{ width: `${run.status === 'summarizing' ? 100 : share}%`, height: '100%', background: 'var(--accent-primary)' }} />
        </div>
      )}
    </div>
  );
}

function StartDialog({ ai, busy, error, onStart, onClose }) {
  return (
    <Dialog size="md" labelledBy="insights-start-title" onClose={busy ? undefined : onClose} confirmOnClose={false}>
      <DialogHeader id="insights-start-title" title="Analyse this lab" icon={<Sparkles size={18} />} onClose={busy ? undefined : onClose} />
      <DialogBody>
        <div style={{ display: 'grid', gap: 12, fontSize: 14, lineHeight: 1.6 }}>
          <p style={{ margin: 0 }}>
            Each student's recording is measured: time spent, runs and their errors, re-runs of unchanged code, errors left
            in the editor, code reworked again and again, pasted code. Their screenshots are cut into scenes wherever the
            screen changed.
          </p>
          {ai?.enabled ? (
            <p style={{ margin: 0 }}>
              With AI, the model <strong>{ai.model}</strong> ({ai.providerLabel || 'AI provider'}, {ai.host}) then reads each student's measurements,
              code and up to {ai.maxImages} screenshots of the key moments, and describes up to {ai.maxScenes} scenes from small
              screenshots{ai.storyModel && ai.storyModel !== ai.model ? ` (with ${ai.storyModel}${ai.storyHost && ai.storyHost !== ai.host ? `, ${ai.storyHost}` : ''})` : ''}
              {!ai.storyModel ? ' (no scene descriptions: the key for them cannot be used)' : ''}, to report where each
              student got stuck and anything worth a closer look, then sums up the class. Student IDs, names and email addresses
              are removed from the text it reads; screenshots are sent as they are. Reports are for staff only and never decide
              misconduct.
            </p>
          ) : (
            <p style={{ margin: 0, color: 'var(--text-secondary)' }}>
              AI analysis is not available{ai?.reason ? `: ${ai.reason}` : '.'} The measurements and the scenes are still made.
            </p>
          )}
          <p style={{ margin: 0, color: 'var(--text-secondary)' }}>It runs in the background; you can leave this page.</p>
          {error && <p role="alert" style={{ margin: 0, color: 'var(--accent-danger)' }}>{error}</p>}
        </div>
      </DialogBody>
      <DialogFooter>
        <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
        <button type="button" className={ai?.enabled ? 'btn btn-secondary' : 'btn btn-primary'} onClick={() => onStart(false)} disabled={busy}>
          Measure only
        </button>
        {ai?.enabled && (
          <button type="button" className="btn btn-primary" onClick={() => onStart(true)} disabled={busy}>
            <Sparkles size={14} aria-hidden="true" /> Analyse with AI
          </button>
        )}
      </DialogFooter>
    </Dialog>
  );
}

export default function LabInsightsCard({ labId }) {
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState(null);
  const query = useQuery({
    queryKey: ['labInsights', labId],
    queryFn: () => labInsights.get(labId),
    refetchInterval: (current) => (ACTIVE.includes(current?.state?.data?.run?.status) ? 5000 : false),
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['labInsights', labId] });
  const start = useMutation({
    mutationFn: (ai) => labInsights.start(labId, { ai }),
    onSuccess: () => { setConfirming(false); setError(null); refresh(); },
    onError: (err) => setError(errorMessage(err, 'Could not start the analysis.')),
  });
  const cancel = useMutation({
    mutationFn: () => labInsights.cancel(labId, query.data?.run?.id),
    onSettled: refresh,
  });
  const data = query.data;
  const run = data?.run || null;
  const active = ACTIVE.includes(run?.status);
  const report = run?.report;
  const verdict = report?.verdict ? VERDICTS[report.verdict] : null;
  // The model writes "Student N": show who that is (the class report links them).
  const names = new Map((data?.students || []).map((s) => [s.label, s.name || s.studentId]));
  const withNames = (text) => String(text || '').replace(/Student \d+/g, (label) => names.get(label) || label);

  return (
    <div className="card" style={{ marginBottom: 24 }} aria-labelledby="lab-insights-title">
      <div className="card-header" style={{ flexWrap: 'wrap', gap: 12 }}>
        <h2 id="lab-insights-title" style={{ fontSize: 18, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Sparkles size={20} aria-hidden="true" /> Lab insights
        </h2>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {run && ['completed', 'failed', 'cancelled'].includes(run.status) && run.progress?.done > 0 && (
            <Link className="btn btn-secondary btn-sm" to={`/labs/${labId}/insights`}>Open the report</Link>
          )}
          {active ? (
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => cancel.mutate()} disabled={cancel.isPending}>
              <Square size={14} aria-hidden="true" /> Stop the analysis
            </button>
          ) : (
            <button type="button" className="btn btn-primary btn-sm" onClick={() => { setError(null); setConfirming(true); }}
              disabled={!data?.canAnalyze || query.isPending}>
              <Sparkles size={14} aria-hidden="true" /> {run ? 'Analyse again' : 'Analyse this lab'}
            </button>
          )}
        </div>
      </div>
      <div style={{ padding: '16px 20px', display: 'grid', gap: 12 }}>
        <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: 14, lineHeight: 1.6, maxWidth: '72ch' }}>
          How each student worked, where they got stuck (an error they could not get past, code re-run without changes, a loop
          or a method they rewrote again and again) and anything worth a closer look, then the same for the class, from what
          the lab recorded.
        </p>
        {data && !data.canAnalyze && <p role="status" style={{ margin: 0, fontSize: 14 }}>{data.reason}</p>}
        <InsightProgress run={run} />
        {run?.status === 'completed' && (
          <div style={{ display: 'grid', gap: 6 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: 13, color: 'var(--text-secondary)' }}>
              {verdict && <Chip color={verdict.color}>{verdict.label}</Chip>}
              <span>Analysed {whenOf(run.completedAt)}{run.model ? ` with ${run.model}${runUse(run).where}` : ', measurements only'}:
                {' '}{run.aggregates?.analysed ?? run.progress?.done} student{(run.aggregates?.analysed ?? 0) === 1 ? '' : 's'}.{runUse(run).tokens}</span>
            </div>
            {report?.overview && <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6, maxWidth: '72ch' }}>{withNames(report.overview)}</p>}
          </div>
        )}
        {run && ['failed', 'cancelled'].includes(run.status) && (
          <p role="status" style={{ margin: 0, fontSize: 14, color: run.status === 'failed' ? 'var(--accent-danger)' : 'var(--text-secondary)' }}>
            {run.status === 'cancelled' ? 'The last analysis was stopped.' : `The last analysis failed: ${run.error || 'unknown error'}`}
          </p>
        )}
        {run?.status === 'completed' && run.error && <p role="status" style={{ margin: 0, fontSize: 13, color: 'var(--accent-warning)' }}>{run.error}</p>}
        {query.isError && <p role="alert" style={{ margin: 0, color: 'var(--accent-danger)' }}>{errorMessage(query.error, 'Could not read the lab\'s insights.')}</p>}
      </div>
      {confirming && (
        <StartDialog ai={data?.ai} busy={start.isPending} error={error} onStart={(ai) => start.mutate(ai)} onClose={() => setConfirming(false)} />
      )}
    </div>
  );
}
